import { isEqual } from 'lodash';
import { findGradingForSize } from '../sampleSizeUtils';

const NON_USER_UPDATE = Symbol('nonUserSpecUpdate');
const TYPING_UPDATE = Symbol('typingSpecUpdate');
const HIGHLIGHT_ROW_FLAGS = ['isSupplierCodeChanged', 'isSupplierDescChanged', 'isUserCodeChanged', 'isUserDescChanged'];

/**
 * Replaces a single row, preserving the object identity of every other row so a
 * memoised row component only re-renders the row that actually changed.
 * @param {Array<object>} specData
 * @param {number} rowIndex
 * @param {object} nextRow
 * @returns {Array<object>} new array, or the original when nothing changed
 */
export const replaceRow = (specData, rowIndex, nextRow) => {
  if (!Array.isArray(specData) || rowIndex < 0 || rowIndex >= specData.length) {
    return specData;
  }
  if (specData[rowIndex] === nextRow) {
    return specData;
  }
  const nextSpecData = specData.slice();
  nextSpecData[rowIndex] = nextRow;
  return nextSpecData;
};

/**
 * Sets one scalar field on one row without cloning the whole spec array.
 * @param {Array<object>} specData
 * @param {number} rowIndex
 * @param {string} key
 * @param {*} value
 * @returns {Array<object>}
 */
export const updateRowField = (specData, rowIndex, key, value) => {
  const row = Array.isArray(specData) ? specData[rowIndex] : null;
  if (!row || row[key] === value) {
    return specData;
  }
  return replaceRow(specData, rowIndex, { ...row, [key]: value });
};

/**
 * Sets several scalar fields on one row in a single immutable pass.
 * @param {Array<object>} specData
 * @param {number} rowIndex
 * @param {object} patch
 * @returns {Array<object>}
 */
export const updateRowFields = (specData, rowIndex, patch) => {
  const row = Array.isArray(specData) ? specData[rowIndex] : null;
  if (!row) {
    return specData;
  }
  const hasChange = Object.keys(patch).some((key) => row[key] !== patch[key]);
  if (!hasChange) {
    return specData;
  }
  return replaceRow(specData, rowIndex, { ...row, ...patch });
};

/**
 * Writes `field` on the grading of `sizeName` for one row, creating the grading when
 * the size has no entry yet. Only the touched row and the touched grading become new
 * objects; sibling rows and sibling gradings keep their identity.
 * @param {Array<object>} specData
 * @param {number} rowIndex
 * @param {string} sizeName
 * @param {string} field
 * @param {*} value
 * @returns {Array<object>}
 */
export const updateRowGrading = (specData, rowIndex, sizeName, field, value) => {
  const row = Array.isArray(specData) ? specData[rowIndex] : null;
  if (!row) {
    return specData;
  }
  const gradings = Array.isArray(row.gradings) ? row.gradings : [];
  /**
   * Paired the same way the cell is rendered, so an edit made while the column and the row disagree
   * on surrounding whitespace lands on the grading the user can see rather than appending a second
   * one for the same size under the column's spelling.
   */
  const existingGrading = findGradingForSize(gradings, sizeName);
  const gradingIndex = existingGrading ? gradings.indexOf(existingGrading) : -1;

  if (gradingIndex === -1) {
    const createdGrading = { sizeName, measurment: '0', grading: '0', isZero: false, isSampleSize: false, [field]: value };
    return replaceRow(specData, rowIndex, { ...row, gradings: [...gradings, createdGrading] });
  }

  if (gradings[gradingIndex][field] === value) {
    return specData;
  }

  const nextGradings = gradings.slice();
  nextGradings[gradingIndex] = { ...gradings[gradingIndex], [field]: value };
  return replaceRow(specData, rowIndex, { ...row, gradings: nextGradings });
};

/**
 * Returns true when a row carries any amber-highlight marker.
 * @param {object} row
 * @returns {boolean}
 */
const rowHasHighlight = (row) => {
  if (HIGHLIGHT_ROW_FLAGS.some((flag) => row?.[flag])) {
    return true;
  }
  return Array.isArray(row?.gradings) && row.gradings.some((grading) => grading?.isUserChanged);
};

/**
 * Drops every highlight marker. Rows that carry no marker keep their identity, so
 * clearing the highlight re-renders only the rows that were actually amber.
 * @param {Array<object>} specData
 * @returns {Array<object>}
 */
export const clearHighlightFlags = (specData) => {
  if (!Array.isArray(specData)) {
    return specData;
  }
  let didChange = false;
  const nextSpecData = specData.map((row) => {
    if (!rowHasHighlight(row)) {
      return row;
    }
    didChange = true;
    const gradings = Array.isArray(row.gradings) ? row.gradings.map((grading) => (grading?.isUserChanged ? { ...grading, isUserChanged: false } : grading)) : row.gradings;
    return {
      ...row,
      isSupplierCodeChanged: false,
      isSupplierDescChanged: false,
      isUserCodeChanged: false,
      isUserDescChanged: false,
      gradings,
    };
  });
  return didChange ? nextSpecData : specData;
};

/**
 * Re-uses the previously rendered row object whenever an incoming server row is
 * field-for-field identical, so a refetch cannot remount rows the user never touched.
 * Comparison is a structural deep-equal, so a differing key order between the server
 * document and the in-memory row does not defeat the reuse.
 * @param {Array<object>} incomingRows
 * @param {Array<object>} previousRows
 * @returns {Array<object>}
 */
export const preserveRowIdentity = (incomingRows, previousRows) => {
  if (!Array.isArray(incomingRows) || !Array.isArray(previousRows) || previousRows.length === 0) {
    return incomingRows;
  }
  const previousByCode = new Map(previousRows.filter((row) => row?.code).map((row) => [row.code, row]));
  return incomingRows.map((row) => {
    const previousRow = row?.code ? previousByCode.get(row.code) : null;
    if (!previousRow) {
      return row;
    }
    return isEqual(previousRow, row) ? previousRow : row;
  });
};

/**
 * Tags an array as produced by the app rather than by a user edit — a server load, a
 * derived recalculation, or a highlight reset. The tag is a non-enumerable symbol that
 * lives on the array itself, so it survives React batching and re-invoked state updaters,
 * and is never copied onto the new arrays that user edits produce.
 * @param {Array<object>} specData
 * @returns {Array<object>} the same array
 */
export const markNonUserUpdate = (specData) => {
  if (Array.isArray(specData)) {
    Object.defineProperty(specData, NON_USER_UPDATE, { value: true, enumerable: false, configurable: true });
  }
  return specData;
};

/**
 * True when this exact array was produced by the app rather than by a user edit.
 * @param {Array<object>} specData
 * @returns {boolean}
 */
export const isNonUserUpdate = (specData) => Boolean(Array.isArray(specData) && specData[NON_USER_UPDATE]);

/**
 * Folds the gradings the server re-derived on save back into the loaded rows, matched by
 * POM code. Client-only markers such as the amber highlight flags are preserved, and a row
 * whose gradings the server did not change keeps its identity so nothing re-renders.
 * @param {Array<object>} specData
 * @param {Array<{code: string, gradings: Array<object>}>} derivedRows
 * @returns {Array<object>}
 */
export const mergeDerivedGradings = (specData, derivedRows) => {
  if (!Array.isArray(specData) || !Array.isArray(derivedRows) || derivedRows.length === 0) {
    return specData;
  }
  const derivedByCode = new Map(derivedRows.filter((row) => row?.code).map((row) => [row.code, row]));
  let didChange = false;

  const nextSpecData = specData.map((row) => {
    const derivedRow = row?.code ? derivedByCode.get(row.code) : null;
    if (!derivedRow || !Array.isArray(derivedRow.gradings) || !Array.isArray(row.gradings)) {
      return row;
    }
    let rowChanged = false;
    const nextGradings = row.gradings.map((grading) => {
      const derivedGrading = derivedRow.gradings.find((candidate) => candidate?.sizeName === grading?.sizeName);
      if (!derivedGrading) {
        return grading;
      }
      const mergedGrading = {
        ...grading,
        measurment: derivedGrading.measurment,
        grading: derivedGrading.grading,
        isZero: derivedGrading.isZero ?? grading.isZero,
        isSampleSize: derivedGrading.isSampleSize ?? grading.isSampleSize,
      };
      if (isEqual(mergedGrading, grading)) {
        return grading;
      }
      rowChanged = true;
      return mergedGrading;
    });
    if (!rowChanged) {
      return row;
    }
    didChange = true;
    return { ...row, gradings: nextGradings };
  });

  return didChange ? nextSpecData : specData;
};

/**
 * Tags an array as the result of a keystroke inside a cell the user is still editing.
 * Typing must not persist on every character — it persists when the cell is left — so the
 * save effect skips these arrays. Like the non-user tag this is a non-enumerable symbol on
 * the array itself, so it never propagates to arrays derived from it.
 * @param {Array<object>} specData
 * @returns {Array<object>} the same array
 */
export const markTypingUpdate = (specData) => {
  if (Array.isArray(specData)) {
    Object.defineProperty(specData, TYPING_UPDATE, { value: true, enumerable: false, configurable: true });
  }
  return specData;
};

/**
 * True when this exact array came from a keystroke rather than a completed edit.
 * @param {Array<object>} specData
 * @returns {boolean}
 */
export const isTypingUpdate = (specData) => Boolean(Array.isArray(specData) && specData[TYPING_UPDATE]);
